import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Lock, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CartPage() {
  const { items, subtotal, updateQty, removeItem, discountCode, setDiscountCode } = useCart();
  const { format: money, currency } = useCurrency();
  const navigate = useNavigate();
  const [code, setCode] = useState(discountCode || "");
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    setError("");
    try {
      const res = await api.post("/discount/validate", {
        code: code.trim(),
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, color: i.color })),
      });
      if (res.data.valid) {
        setApplied(res.data);
        setDiscountCode(res.data.code);
        toast.success(`Code ${res.data.code} applied — ${res.data.percent}% off`);
      } else {
        setApplied(null);
        setDiscountCode("");
        let msg = res.data.message || "Invalid code";
        if (res.data.reason === "below_min" && currency !== "CAD" && res.data.min_subtotal) {
          msg = `Error: for this code to be applied, your cart's subtotal must be ${money(res.data.min_subtotal)}+`;
        }
        setError(msg);
      }
    } catch {
      setApplied(null);
      setDiscountCode("");
      setError("Could not apply code.");
    } finally {
      setApplying(false);
    }
  };

  const removeCode = () => { setApplied(null); setCode(""); setError(""); setDiscountCode(""); };

  const discount = applied ? applied.discount_amount : 0;
  const total = applied ? applied.total : subtotal;

  if (items.length === 0) {
    return (
      <div className="container-lira py-32 text-center" data-testid="cart-empty">
        <h1 className="font-serif text-4xl md:text-5xl font-light">Your bag is empty</h1>
        <p className="mt-4 text-[#737373]">Timeless essentials await.</p>
        <Link to="/collection/all" className="btn-primary mt-10 inline-flex">Shop the Collection</Link>
      </div>
    );
  }

  return (
    <div className="container-lira py-14 md:py-20" data-testid="cart-page">
      <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight mb-12">Shopping Bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
        <div className="divide-y divide-[#E5E5E5] border-y border-[#E5E5E5]">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.color}-${i.size}`} className="flex gap-5 py-6" data-testid="cart-page-item">
              <Link to={`/product/${i.slug}`} className="w-28 aspect-[3/4] bg-beige rounded-sm overflow-hidden shrink-0">
                <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-3">
                  <div>
                    <Link to={`/product/${i.slug}`} className="font-serif text-xl">{i.name}</Link>
                    {(i.color || i.size) && <p className="text-sm text-[#737373] mt-1">{[i.color, i.size].filter(Boolean).join(" · ")}</p>}
                  </div>
                  <span className="text-sm">{money(i.price * i.quantity)}</span>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-[#E5E5E5] rounded-full">
                    <button className="px-3 py-2" onClick={() => updateQty(i.product_id, i.color, i.size, i.quantity - 1)} aria-label="Decrease"><Minus strokeWidth={1} className="h-3.5 w-3.5" /></button>
                    <span className="text-sm w-6 text-center">{i.quantity}</span>
                    <button className="px-3 py-2" onClick={() => updateQty(i.product_id, i.color, i.size, i.quantity + 1)} aria-label="Increase"><Plus strokeWidth={1} className="h-3.5 w-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(i.product_id, i.color, i.size)} className="flex items-center gap-2 text-xs text-[#737373] hover:text-[#1A1A1A]" data-testid="cart-page-remove">
                    <Trash2 strokeWidth={1} className="h-4 w-4" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="bg-beige rounded-sm p-8 h-fit">
          <h2 className="eyebrow mb-6">Order Summary</h2>

          {/* Discount code */}
          <div className="mb-6">
            {applied ? (
              <div className="flex items-center justify-between bg-white border border-[#d8d1c4] rounded-md px-4 py-3" data-testid="applied-discount">
                <span className="text-sm">Code <strong>{applied.code}</strong> · {applied.percent}% off</span>
                <button onClick={removeCode} className="text-xs text-[#737373] hover:text-[#1A1A1A] underline" data-testid="remove-discount">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCode()}
                  placeholder="Discount code"
                  className="flex-1 bg-white border border-[#d8d1c4] rounded-md px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black uppercase tracking-wide"
                  data-testid="discount-input"
                />
                <button onClick={applyCode} disabled={applying} className="btn-secondary px-5 py-2.5 disabled:opacity-60" data-testid="apply-discount">
                  {applying ? "…" : "Apply"}
                </button>
              </div>
            )}
            {error && !applied && (
              <p className="mt-2 text-sm text-[#9a3b3b]" data-testid="discount-error">{error}</p>
            )}
          </div>

          <div className="space-y-4 text-sm">
            <div className="flex justify-between"><span className="text-[#5a5a5a]">Subtotal</span><span>{money(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#3a6b45]" data-testid="discount-line"><span>Discount ({applied.percent}%)</span><span>−{money(discount)}</span></div>
            )}
            <div className="flex justify-between text-[#737373]"><span>Shipping &amp; tax</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between text-lg pt-4 border-t border-[#d8d1c4]"><span>Total ({currency})</span><span data-testid="cart-total">{money(total)}</span></div>
            <p className="text-xs text-[#737373]">Shipping &amp; taxes are calculated on the next step based on your address. Prices shown in {currency}.</p>
          </div>

          <button
            onClick={() => navigate("/checkout")}
            className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
            data-testid="checkout-button"
          >
            Checkout <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
          </button>

          <p className="flex items-center justify-center gap-2 text-xs text-[#737373] mt-4"><Lock strokeWidth={1} className="h-3.5 w-3.5" /> Secure checkout · PayPal &amp; cards</p>
          <Link to="/collection/all" className="block text-center eyebrow mt-6">Continue Shopping</Link>
        </aside>
      </div>
    </div>
  );
}
